import type { WorkflowAction } from '@cognitoforms/types/server-types/forms/model/workflow-action';
import type { Order } from '@cognitoforms/types/server-types/payment/model/order';
import type { TransactionFee } from '@cognitoforms/types/server-types/payment/model/transaction-fee';
import type { WorkflowActionExtensions } from './workflow-action-extensions';
import type { FormEntryStatus } from '@cognitoforms/types/server-types/forms/model/form-entry-status';
import type { FormEntry } from '@cognitoforms/types/server-types/forms/model/form-entry';
import type { EntityOfType } from '@cognitoforms/model.js';
import type { OrderExtensions } from 'src/framework/model/extensions/payment-extensions';

export interface FormEntryExtensions {
	Page_Index: { [key: string]: number };
}

type FormEntryOrderProperty<TOrder extends Order> = {
	Order: TOrder
};

export type FormEntryWithOrder = FormEntry & FormEntryOrderProperty<Order & OrderExtensions> & FormEntryOrderPsuedoProperties;

export interface FormEntryPsuedoProperties {
	Form_Available: boolean;
}

export interface FormEntryRuleMethods {
	initDefaultAction(): void;
}

export type FormEntryWithOptionalOrder = FormEntry & Partial<FormEntryOrderProperty<Order & OrderExtensions> & FormEntryOrderPsuedoProperties>;

export function isEntryWithOrder(entry: EntityOfType<FormEntryWithOptionalOrder>): entry is EntityOfType<FormEntryWithOrder> {
	return !!entry.Order;
}

export interface FormEntryWorkflowExtensions {
	Form_ReadOnly: boolean;
	Workflow_Actions: (WorkflowAction & WorkflowActionExtensions)[];
	Action_IsLocked: boolean;
	Attempted_Action: string;
	Allowed_Actions: (WorkflowAction & WorkflowActionExtensions)[];
	Entry_Statuses: FormEntryStatus[];
	Next_Status: string;
	Allow_Workflow_Links: boolean;
	Allow_Workflow_Links_Role_Dependent: boolean;
}

export interface FormEntryOrderPsuedoProperties {
	Save_Customer_Card: boolean;
	Save_Card_Agreement: boolean;
	Require_Payment: boolean;
	Require_Payment_Expression: boolean;
	Billing_Address_Field: string;
	Billing_Email_Field: string;
	Billing_Name_Field: string;
	Billing_Phone_Field: string;
	Transaction_Fees: TransactionFee[];
	Processing_Fees: TransactionFee[];
	Domestic_Country_Code: string;
	Application_Fee: TransactionFee;
	Payment_Processor_Name: string;
	Line_Item_Metadata: LineItemMetaData[];
	Processing_Fee_Description: string;
	European_Countries: string[];
	Processor_Fee_Modes: { [key: string]: number };
}

export type LineItemMetaData = {
	id: number[];
	path: string;
	quantitySelected: string;
	allowNegatives: boolean;
	descriptionProperty: string;
	nameProperty: string;
	amountProperty: string;
	type: string;
	isWithinTable: boolean;
	lookup?: LookupLineItemMetaData;
}

type LookupLineItemMetaData = {
	pricePath: string;
	labelFormat: string;
}
